import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import './FundApprovals.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));

const CURRENCY_SYMBOLS = {
  INR: '₹', USD: '$', LRD: 'L$', XOF: 'CFA', EUR: '€', GBP: '£', GHS: 'GH₵', NGN: '₦',
};
const symbolFor = (code) => CURRENCY_SYMBOLS[code] || (code ? `${code} ` : '');

const FundApprovals = ({ onWalletUpdate }) => {
  const { currentUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [reasonMap, setReasonMap] = useState({});
  const [busyId, setBusyId] = useState(null);

  const perms = currentUser?.permissions || ['full'];
  const has = (cap) => perms.includes('full') || perms.includes(cap);

  const getAuthHeader = () => ({
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/api/admin/funds/requests`, getAuthHeader());
      if (res.data.success) setRequests(res.data.requests || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const doAction = async (id, actionFn, successMsg) => {
    setBusyId(id);
    setError('');
    setMsg('');
    try {
      await actionFn();
      setMsg(successMsg);
      setReasonMap((m) => ({ ...m, [id]: '' }));
      await fetchRequests();
      if (onWalletUpdate) onWalletUpdate();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const approve1 = (id) => doAction(id,
    () => axios.post(`${API_BASE}/api/admin/funds/approve1/${id}`, {}, getAuthHeader()),
    'Request approved (step 1)');

  const approveFinal = (id) => doAction(id,
    () => axios.post(`${API_BASE}/api/admin/funds/approve-final/${id}`, {}, getAuthHeader()),
    'Fund request executed');

  const reject = (id) => doAction(id,
    () => axios.post(`${API_BASE}/api/admin/funds/reject/${id}`, { reason: reasonMap[id] || null }, getAuthHeader()),
    'Fund request rejected');

  return (
    <div className="fund-approvals">
      <div className="fa-header">
        <h2>Fund Approval Queue</h2>
        <p>Review and approve deposit / withdrawal requests (3-step approval).</p>
      </div>

      {error && <div className="fa-alert fa-alert-error"><i className="fas fa-exclamation-triangle"></i> {error}</div>}
      {msg && <div className="fa-alert fa-alert-success"><i className="fas fa-check-circle"></i> {msg}</div>}

      {loading ? (
        <div className="fa-empty"><i className="fas fa-spinner fa-spin"></i> Loading requests…</div>
      ) : requests.length === 0 ? (
        <div className="fa-empty"><i className="fas fa-inbox"></i> No pending fund requests.</div>
      ) : (
        <div className="fa-list">
          {requests.map((r) => {
            const isPending = r.status === 'pending';
            const isApproved1 = r.status === 'approved_1';
            const action = (r.action === 'debit' || r.action === 'withdraw') ? 'withdraw' : 'deposit';
            const busy = busyId === r.id;

            return (
              <div key={r.id} className="fa-card">
                <div className="fa-card-main">
                  <div className={`fa-type fa-type-${action}`}>
                    {action === 'deposit' ? '↓ Deposit' : '↑ Withdraw'}
                  </div>
                  <div className="fa-amount">
                    {symbolFor(r.currency)}{Number(r.amount).toFixed(2)}
                    <span className="fa-currency"> {r.currency}</span>
                  </div>
                  <div className="fa-parties">
                    <div><label>Player</label><span>{r.target_username || `#${r.target_user_id}`}</span></div>
                    <div><label>Requested by</label><span>{r.initiator_username || `#${r.admin_id}`}</span></div>
                  </div>
                  <div className={`fa-status fa-status-${r.status}`}>
                    {isPending ? 'Awaiting 1st approval' : isApproved1 ? 'Awaiting final approval' : r.status}
                  </div>
                </div>

                <div className="fa-card-actions">
                  {isPending && has('cash.approve1') && (
                    <button className="fa-btn fa-btn-approve" disabled={busy} onClick={() => approve1(r.id)}>
                      {busy ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />} Approve (1st)
                    </button>
                  )}

                  {isApproved1 && has('cash.approve_final') && (
                    <button className="fa-btn fa-btn-execute" disabled={busy} onClick={() => approveFinal(r.id)}>
                      {busy ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check-double" />} Approve &amp; Execute
                    </button>
                  )}

                  {((isPending && has('cash.approve1')) || (isApproved1 && has('cash.approve_final'))) && (
                    <>
                      <input
                        type="text"
                        className="fa-reason"
                        placeholder="Reject reason (optional)"
                        value={reasonMap[r.id] || ''}
                        onChange={(e) => setReasonMap((m) => ({ ...m, [r.id]: e.target.value }))}
                      />
                      <button className="fa-btn fa-btn-reject" disabled={busy} onClick={() => reject(r.id)}>
                        <i className="fas fa-times" /> Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FundApprovals;
